import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Settings,
  LogOut,
  Users,
  ClipboardList,
  LayoutDashboard,
  Brain,
  Shield,
  UserPlus,
  Wallet,
  CalendarClock,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/features/auth';
import { useCompany } from '@/context/CompanyContext';
import { usePermissions } from '@/hooks/usePermissions';
import { useMobileMenu } from '@/context/MobileMenuContext';

interface NavItem {
  nameKey: string;
  path: string;
  icon: React.ReactNode;
  requiresSuperAdmin?: boolean;
}

const HR_NAV_ITEMS: NavItem[] = [
  {
    nameKey: 'nav.dashboard',
    path: '/app/hr',
    icon: <LayoutDashboard className="w-5 h-5" />,
  },
  {
    nameKey: 'nav.recruitment',
    path: '/app/hr/recruitment',
    icon: <UserPlus className="w-5 h-5" />,
  },
  {
    nameKey: 'nav.payroll',
    path: '/app/hr/payroll',
    icon: <Wallet className="w-5 h-5" />,
  },
  {
    nameKey: 'nav.shiftManagement',
    path: '/app/hr/shifts',
    icon: <CalendarClock className="w-5 h-5" />,
  },
  {
    nameKey: 'nav.actionLog',
    path: '/app/hr/action-log',
    icon: <ClipboardList className="w-5 h-5" />,
  },
];

const UTILITY_NAV_ITEMS: NavItem[] = [
  {
    nameKey: 'nav.aiReports',
    path: '/app/reports/ai',
    icon: <Brain className="w-5 h-5" />,
  },
  {
    nameKey: 'nav.settings',
    path: '/app/settings',
    icon: <Settings className="w-5 h-5" />,
  },
  {
    nameKey: 'nav.superAdmin',
    path: '/app/admin',
    icon: <Shield className="w-5 h-5" />,
    requiresSuperAdmin: true,
  },
];

export function HRSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut, user } = useAuth();
  const { t } = useTranslation();
  const { activeCompanyRole } = useCompany();
  const { isPremium, isSuperAdmin } = usePermissions();
  const { isMobileMenuOpen, setIsMobileMenuOpen } = useMobileMenu();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const isActive = (path: string): boolean => {
    if (path === '/app/hr') return location.pathname === '/app/hr';
    return location.pathname.startsWith(path);
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  const renderItem = (item: NavItem) => {
    if (item.requiresSuperAdmin && !isSuperAdmin) return null;
    return (
      <Link
        key={item.path}
        to={item.path}
        onClick={() => setIsMobileMenuOpen(false)}
        className={`
          flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200
          ${
            isActive(item.path)
              ? 'bg-primary-100 text-primary-700 font-medium'
              : 'text-gray-700 hover:bg-gray-100'
          }
        `}
      >
        {item.icon}
        <span>{t(item.nameKey)}</span>
      </Link>
    );
  };

  return (
    <>
      {isMobileMenuOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      <aside
        className={`
          fixed md:static inset-y-0 right-0 z-40 md:z-30
          w-64 bg-white border-l md:border-l-0 md:border-r border-gray-200
          transform transition-transform duration-300 ease-in-out
          ${isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full md:translate-x-0'}
          flex flex-col
        `}
      >
        <div className="h-16 flex items-center justify-center border-b border-gray-200 px-6">
          <Link to="/app/hr" className="flex items-center gap-2">
            <Users className="w-8 h-8 text-primary-600" />
            <span className="text-xl font-bold text-gray-900">Axelio</span>
          </Link>
        </div>

        <nav className="flex-1 overflow-y-auto p-4 space-y-1">
          {HR_NAV_ITEMS.map(renderItem)}

          <div className="my-3 border-t border-gray-200" />

          {UTILITY_NAV_ITEMS.map(renderItem)}
        </nav>

        <div className="border-t border-gray-200 p-4 space-y-3">
          {(isPremium || isSuperAdmin) && (
            <div className="px-3 py-2 bg-gradient-to-r from-amber-50 to-amber-100 rounded-lg border border-amber-200">
              <p className="text-xs font-semibold text-amber-800 text-center">
                {isSuperAdmin ? 'Super Admin' : 'Premium'}
              </p>
            </div>
          )}

          {activeCompanyRole && (
            <div className="px-3 py-2 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-xs font-semibold text-blue-700 text-center capitalize">
                {activeCompanyRole}
              </p>
            </div>
          )}

          {user?.email && (
            <div className="px-3 py-2 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-600 truncate text-center">
                {user.email}
              </p>
            </div>
          )}

          <button
            onClick={handleLogout}
            disabled={isLoggingOut}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <LogOut className="w-4 h-4" />
            <span>{isLoggingOut ? 'Logging out...' : t('nav.logout')}</span>
          </button>
        </div>
      </aside>
    </>
  );
}
